import { motion as Motion } from 'framer-motion';

/* ── Injected styles ── */
const SUCCESS_STYLES = `
  .ss-wrap {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    text-align: center;
    gap: 1.2rem;
    padding: 3rem 2rem;
    min-height: 340px;
  }

  .ss-circle {
    width: 72px;
    height: 72px;
    border-radius: 50%;
    background: radial-gradient(circle at 40% 40%, rgba(0,229,255,0.22), rgba(0,229,255,0.06));
    border: 1.5px solid rgba(0,229,255,0.35);
    display: flex;
    align-items: center;
    justify-content: center;
    box-shadow:
      0 0 0 12px rgba(0,229,255,0.06),
      0 0 32px rgba(0,229,255,0.18);
  }

  .ss-check {
    stroke: #00e5ff;
    stroke-width: 2.5;
    stroke-linecap: round;
    stroke-linejoin: round;
    fill: none;
    strokeDasharray: 40;
    strokeDashoffset: 40;
  }

  .ss-heading {
    font-family: 'Instrument Serif', 'Georgia', serif;
    font-size: clamp(1.6rem, 4vw, 2rem);
    color: var(--text-strong, #f3fbff);
    margin: 0;
    line-height: 1.1;
  }

  .ss-body {
    font-family: 'Manrope', 'Inter', sans-serif;
    font-size: 0.95rem;
    color: rgba(185, 210, 228, 0.65);
    max-width: 320px;
    margin: 0;
    line-height: 1.7;
  }

  .ss-reset {
    margin-top: 0.5rem;
    font-family: 'JetBrains Mono', monospace;
    font-size: 12px;
    letter-spacing: 0.1em;
    text-transform: uppercase;
    color: rgba(0, 229, 255, 0.7);
    background: transparent;
    border: 1px solid rgba(0, 229, 255, 0.22);
    border-radius: 8px;
    padding: 8px 20px;
    cursor: pointer;
    transition: all 0.22s ease;
  }

  .ss-reset:hover {
    color: #00e5ff;
    border-color: rgba(0, 229, 255, 0.5);
    background: rgba(0, 229, 255, 0.06);
    box-shadow: 0 0 16px rgba(0, 229, 255, 0.12);
  }
`;

let injected = false;
function injectStyles() {
  if (injected || document.getElementById('ss-styles')) return;
  const tag = document.createElement('style');
  tag.id = 'ss-styles';
  tag.textContent = SUCCESS_STYLES;
  document.head.appendChild(tag);
  injected = true;
}

/* ── Animated check path ── */
function AnimatedCheck() {
  return (
    <svg width="36" height="36" viewBox="0 0 36 36" aria-hidden="true">
      <Motion.path
        d="M8 18 L15 25 L28 11"
        className="ss-check"
        initial={{ pathLength: 0, opacity: 0 }}
        animate={{ pathLength: 1, opacity: 1 }}
        transition={{ duration: 0.55, delay: 0.25, ease: [0.22, 1, 0.36, 1] }}
        stroke="#00e5ff"
        strokeWidth="2.5"
        strokeLinecap="round"
        strokeLinejoin="round"
        fill="none"
      />
    </svg>
  );
}

/* ── Component ── */
export default function SuccessState({ onReset }) {
  injectStyles();

  return (
    <Motion.div
      className="ss-wrap"
      initial={{ opacity: 0, scale: 0.88, y: 16 }}
      animate={{ opacity: 1, scale: 1, y: 0 }}
      transition={{ duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
    >
      {/* Checkmark circle */}
      <Motion.div
        className="ss-circle"
        initial={{ scale: 0.6, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
      >
        <AnimatedCheck />
      </Motion.div>

      {/* Copy */}
      <Motion.h3
        className="ss-heading"
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3, duration: 0.45 }}
      >
        Message Sent!
      </Motion.h3>

      <Motion.p
        className="ss-body"
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.4, duration: 0.45 }}
      >
        Thank you for reaching out. We review every message personally and typically
        reply within one business day.
      </Motion.p>

      {/* Reset button */}
      <Motion.button
        type="button"
        className="ss-reset"
        onClick={onReset}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.55 }}
        aria-label="Send another message"
      >
        Send another message
      </Motion.button>
    </Motion.div>
  );
}
